import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon, PlusIcon, TrashIcon, MagnifyingGlassIcon, ChevronDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { patientService, consultationService } from '../../services/api';
import {
  FactureProforma,
  CreateProformaData,
  Patient,
  TreatmentType,
} from '../../types';

interface LigneFormData {
  description: string;
  quantite: number;
  prix_unitaire: number;
  treatment_type?: number;
}

interface ProformaFormProps {
  proforma?: FactureProforma | null;
  patientId?: number;
  onSubmit: (data: CreateProformaData) => void;
  onClose: () => void;
  /** Erreur renvoyée par l'API : le modal reste ouvert pour permettre la correction */
  error?: string | null;
  isLoading?: boolean;
}

const ProformaForm: React.FC<ProformaFormProps> = ({
  proforma,
  patientId,
  onSubmit,
  onClose,
  error,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    patient: proforma?.patient || patientId || 0,
    date_validite: proforma?.date_validite || getDefaultValidityDate(),
    objet: proforma?.objet || '',
    notes: proforma?.notes || '',
    conditions: proforma?.conditions || 'Ce devis est valable pour une durée de 30 jours à compter de sa date d\'émission.',
    pourcentage_remise: proforma?.pourcentage_remise || 0,
    pourcentage_assurance: proforma?.pourcentage_assurance || 0,
  });

  const [lignes, setLignes] = useState<LigneFormData[]>(
    proforma?.lignes?.map(l => ({
      description: l.description,
      quantite: l.quantite,
      prix_unitaire: Number(l.prix_unitaire),
      treatment_type: l.treatment_type,
    })) || [{ description: '', quantite: 1, prix_unitaire: 0 }]
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Patient search state
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const patientDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch patients
  const { data: patientsData } = useQuery({
    queryKey: ['patients-list'],
    queryFn: () => patientService.getAll({ page_size: 1000 }),
  });

  // Fetch treatment types
  const { data: treatmentTypesData } = useQuery({
    queryKey: ['treatment-types'],
    queryFn: () => consultationService.getTreatmentTypes(),
  });

  const patients = patientsData?.data?.results || [];
  const treatmentTypes = treatmentTypesData?.data || [];

  // Filter patients based on search
  const filteredPatients = patients.filter((p: Patient) => {
    const fullName = `${p.nom} ${p.prenoms}`.toLowerCase();
    const searchLower = patientSearch.toLowerCase();
    return fullName.includes(searchLower) || p.telephone?.includes(patientSearch);
  });

  // Get selected patient name
  const selectedPatient = patients.find((p: Patient) => p.id === formData.patient);

  // Initialize search with selected patient name and pre-fill insurance rate
  useEffect(() => {
    if (selectedPatient && !patientSearch) {
      setPatientSearch(`${selectedPatient.nom} ${selectedPatient.prenoms}`);

      // Pré-remplir le taux d'assurance si c'est un nouveau devis et que le patient est assuré
      if (!proforma && selectedPatient.assureur && selectedPatient.assureur_details) {
        setFormData(prev => ({
          ...prev,
          pourcentage_assurance: Number(selectedPatient.assureur_details?.taux_couverture) || 0
        }));
      }
    }
  }, [selectedPatient, proforma]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(event.target as Node)) {
        setShowPatientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePatientSelect = (patient: Patient) => {
    // Pré-remplir le taux d'assurance si le patient est assuré
    const tauxAssurance = patient.assureur_details?.taux_couverture || 0;

    setFormData(prev => ({
      ...prev,
      patient: patient.id,
      // Pré-remplir le pourcentage d'assurance si le patient a un assureur
      pourcentage_assurance: patient.assureur ? Number(tauxAssurance) : prev.pourcentage_assurance
    }));
    setPatientSearch(`${patient.nom} ${patient.prenoms}`);
    setShowPatientDropdown(false);
    if (errors.patient) {
      setErrors(prev => ({ ...prev, patient: '' }));
    }
  };

  function getDefaultValidityDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  }

  // Calcul des montants
  const montant_actes = lignes.reduce((sum, ligne) => sum + (ligne.quantite * ligne.prix_unitaire), 0);
  const montant_remise = (montant_actes * formData.pourcentage_remise) / 100;
  const montant_apres_remise = montant_actes - montant_remise;
  const montant_assurance = (montant_apres_remise * formData.pourcentage_assurance) / 100;
  const montant_patient = montant_apres_remise - montant_assurance;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLigneChange = (index: number, field: keyof LigneFormData, value: any) => {
    const newLignes = [...lignes];
    newLignes[index] = { ...newLignes[index], [field]: value };

    // Si on sélectionne un type de traitement, remplir la description et le prix
    if (field === 'treatment_type' && value) {
      const treatment = treatmentTypes.find((t: TreatmentType) => t.id === parseInt(value));
      if (treatment) {
        newLignes[index].description = treatment.name;
        newLignes[index].prix_unitaire = Number(treatment.base_price);
      }
    }

    setLignes(newLignes);
  };

  const addLigne = () => {
    setLignes([...lignes, { description: '', quantite: 1, prix_unitaire: 0 }]);
  };

  const removeLigne = (index: number) => {
    if (lignes.length > 1) {
      setLignes(lignes.filter((_, i) => i !== index));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.patient) {
      newErrors.patient = 'Le patient est requis';
    }
    if (!formData.date_validite) {
      newErrors.date_validite = 'La date de validité est requise';
    }
    if (lignes.length === 0 || lignes.every(l => !l.description)) {
      newErrors.lignes = 'Au moins une prestation est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData: CreateProformaData = {
        patient: formData.patient,
        date_validite: formData.date_validite,
        montant_actes,
        montant_total: montant_actes,
        pourcentage_remise: formData.pourcentage_remise,
        montant_remise,
        pourcentage_assurance: formData.pourcentage_assurance,
        montant_assurance,
        montant_patient,
        objet: formData.objet,
        notes: formData.notes,
        conditions: formData.conditions,
        lignes: lignes.filter(l => l.description).map(l => ({
          description: l.description,
          quantite: l.quantite,
          prix_unitaire: l.prix_unitaire,
          treatment_type: l.treatment_type,
        })),
      };
      onSubmit(submitData);
    }
  };

  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(montant);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-5 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-lg bg-white mb-10">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {proforma ? 'Modifier le devis' : 'Nouveau devis (Pro Forma)'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Patient et Date */}
          <div className="grid grid-cols-2 gap-4">
            <div ref={patientDropdownRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient *</label>
              {patientId ? (
                <input
                  type="text"
                  value={selectedPatient ? `${selectedPatient.nom} ${selectedPatient.prenoms}` : ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl bg-gray-100 text-gray-700"
                />
              ) : (
                <>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={patientSearch}
                      onChange={(e) => {
                        setPatientSearch(e.target.value);
                        setShowPatientDropdown(true);
                        if (!e.target.value) {
                          setFormData(prev => ({ ...prev, patient: 0 }));
                        }
                      }}
                      onFocus={() => setShowPatientDropdown(true)}
                      className={`w-full pl-9 pr-8 py-2 border rounded-md text-gray-700 ${errors.patient ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Rechercher un patient..."
                    />
                    <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                  {showPatientDropdown && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                      {filteredPatients.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Aucun patient trouvé</div>
                      ) : (
                        filteredPatients.map((p: Patient) => (
                          <div
                            key={p.id}
                            onClick={() => handlePatientSelect(p)}
                            className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${
                              formData.patient === p.id ? 'bg-blue-100 text-blue-700' : 'text-gray-700'
                            }`}
                          >
                            <div className="font-medium">{p.nom} {p.prenoms}</div>
                            {p.telephone && <div className="text-xs text-gray-500">{p.telephone}</div>}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
              {errors.patient && <p className="text-red-500 text-xs mt-1">{errors.patient}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valide jusqu'au *</label>
              <input
                type="date"
                name="date_validite"
                value={formData.date_validite}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-gray-700 ${errors.date_validite ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.date_validite && <p className="text-red-500 text-xs mt-1">{errors.date_validite}</p>}
            </div>
          </div>

          {/* Information assurance du patient */}
          {selectedPatient?.assureur && selectedPatient?.assureur_details && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Patient assuré - {selectedPatient.assureur_details.nom}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    N° Assuré: {selectedPatient.numero_assure || 'Non renseigné'}
                    {selectedPatient.nom_souscripteur && ` • Souscripteur: ${selectedPatient.nom_souscripteur}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-blue-800">
                    Taux de couverture: {selectedPatient.assureur_details.taux_couverture}%
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Objet */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Objet du devis</label>
            <input
              type="text"
              name="objet"
              value={formData.objet}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
              placeholder="Ex: Traitement orthodontique, Soins dentaires..."
            />
          </div>

          {/* Lignes de prestations */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Prestations *</label>
              <button
                type="button"
                onClick={addLigne}
                className="flex items-center text-sm text-blue-600 hover:text-blue-800"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Ajouter une ligne
              </button>
            </div>

            <div className="border rounded-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Qté</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32">Prix unit.</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32">Total</th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lignes.map((ligne, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2">
                        <select
                          value={ligne.treatment_type || ''}
                          onChange={(e) => handleLigneChange(index, 'treatment_type', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-700"
                        >
                          <option value="">Manuel</option>
                          {treatmentTypes.map((t: TreatmentType) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={ligne.description}
                          onChange={(e) => handleLigneChange(index, 'description', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-700"
                          placeholder="Description"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={ligne.quantite}
                          onChange={(e) => handleLigneChange(index, 'quantite', parseInt(e.target.value) || 1)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-700"
                          min="1"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={ligne.prix_unitaire}
                          onChange={(e) => handleLigneChange(index, 'prix_unitaire', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-700"
                          min="0"
                        />
                      </td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">
                        {formatMontant(ligne.quantite * ligne.prix_unitaire)} GNF
                      </td>
                      <td className="px-3 py-2">
                        {lignes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLigne(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {errors.lignes && <p className="text-red-500 text-xs mt-1">{errors.lignes}</p>}
          </div>

          {/* Remise et Assurance */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remise (%)</label>
              <input
                type="number"
                name="pourcentage_remise"
                value={formData.pourcentage_remise}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prise en charge assurance (%)</label>
              <input
                type="number"
                name="pourcentage_assurance"
                value={formData.pourcentage_assurance}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
                min="0"
                max="100"
              />
            </div>
          </div>

          {/* Récapitulatif */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Récapitulatif</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Montant des actes:</span>
                <span className="font-medium">{formatMontant(montant_actes)} GNF</span>
              </div>
              {formData.pourcentage_remise > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Remise ({formData.pourcentage_remise}%):</span>
                  <span>-{formatMontant(montant_remise)} GNF</span>
                </div>
              )}
              {formData.pourcentage_assurance > 0 && (
                <div className="flex justify-between text-blue-600">
                  <span>Prise en charge assurance ({formData.pourcentage_assurance}%):</span>
                  <span>-{formatMontant(montant_assurance)} GNF</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2 text-lg font-bold">
                <span>Montant à payer:</span>
                <span className="text-orange-600">{formatMontant(montant_patient)} GNF</span>
              </div>
            </div>
          </div>

          {/* Notes et Conditions */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
                placeholder="Notes internes..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Conditions</label>
              <textarea
                name="conditions"
                value={formData.conditions}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
              />
            </div>
          </div>

          {/* Erreur renvoyée par le serveur */}
          {error && (
            <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Boutons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50"
            >
              {isLoading ? 'Enregistrement...' : proforma ? 'Modifier' : 'Créer le devis'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProformaForm;
